import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import PhotoUploader from "@/components/base/PhotoUploader";
import Modal from "@/components/base/Modal";
import Tabs from "@/components/base/Tabs";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Input, Select } from "@/components/base/Field";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import {
  addStaff,
  addStaffFunction,
  deleteStaff,
  deleteStaffFunction,
  getSalaries,
  getStaff,
  getStaffFunctions,
  saveStaffSalary,
  updateStaff,
  updateStaffFunction,
} from "@/services/hrService";
import { formatDate, formatMoney, plural } from "@/utils/format";
import type { Staff } from "@/types";
import { getCycles } from "@/services/schoolService";
import { EMPLOYMENT_STATUSES, EMPLOYMENT_STATUS_LABEL, EMPLOYMENT_STATUS_TONE } from "@/utils/hr";
import { useUrlSearch } from "@/hooks/useUrlSearch";
import { currencySymbol } from "@/utils/currency";

async function load() {
  const [staff, functions, salaries, cycles] = await Promise.all([
    getStaff(),
    getStaffFunctions(),
    getSalaries(),
    getCycles(),
  ]);
  return { staff, functions, salaries, cycles };
}

const empty = {
  lastName: "",
  firstName: "",
  gender: "M" as Staff["gender"],
  functionId: "",
  cycleId: "",
  phone: "",
  email: "",
  photo: "",
  hireDate: "",
  status: "ACTIVE" as Staff["status"],
};

export default function StaffPage() {
  const { notify } = useToast();
  const { data, loading, reload } = useAsync(load, []);
  const [tab, setTab] = useState("liste");
  const [search, setSearch] = useUrlSearch();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const pager = usePager();
  const [form, setForm] = useState({ ...empty });
  const [fnOpen, setFnOpen] = useState(false);
  const [fnEditing, setFnEditing] = useState<string | null>(null);
  const [fnName, setFnName] = useState("");
  const [fnToDelete, setFnToDelete] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Staff | null>(null);
  const [salaryEdit, setSalaryEdit] = useState<{ id?: string; personId: string; cycleId: string; amount: string; name: string } | null>(null);

  const staffList = useMemo(() => data?.staff ?? [], [data]);
  const functions = data?.functions ?? [];
  const salaries = data?.salaries ?? [];
  const cycles = data?.cycles ?? [];

  const term = search.trim().toLowerCase();
  const filteredStaff = useMemo(
    () =>
      staffList.filter(
        (m) =>
          !term ||
          `${m.firstName} ${m.lastName}`.toLowerCase().includes(term) ||
          m.phone.toLowerCase().includes(term),
      ),
    [staffList, term],
  );

  if (loading || !data) return <LoadingState label="Chargement du personnel..." />;
  const page = pager.slice(filteredStaff, term);

  const fnName_of = (id: string) => functions.find((f) => f.id === id)?.name ?? "—";
  const salaryOf = (personId: string) => salaries.find((s) => s.personId === personId);

  const openForm = (member: Staff | null) => {
    setEditing(member);
    setForm(
      member
        ? {
            lastName: member.lastName,
            firstName: member.firstName,
            gender: member.gender,
            functionId: member.functionId,
            cycleId: member.cycleId,
            phone: member.phone,
            email: member.email,
            photo: member.photo ?? "",
            hireDate: member.hireDate,
            status: member.status,
          }
        : { ...empty, functionId: functions[0]?.id ?? "", cycleId: cycles[0]?.id ?? "" },
    );
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.lastName.trim() || !form.firstName.trim()) {
      notify("Le nom et le prénom sont obligatoires.", "error");
      return;
    }
    if (editing) {
      await updateStaff(editing.id, form);
      notify("Fiche personnel mise à jour.");
    } else {
      await addStaff(form);
      notify(`Membre du personnel ${form.firstName} ${form.lastName} ajouté.`);
    }
    await reload();
    setFormOpen(false);
  };

  const handleFnSave = async () => {
    if (!fnName.trim()) {
      notify("Le nom de la fonction est obligatoire.", "error");
      return;
    }
    if (fnEditing) {
      await updateStaffFunction(fnEditing, fnName.trim());
      notify("Fonction mise à jour.");
    } else {
      await addStaffFunction(fnName.trim());
      notify(`Fonction « ${fnName} » ajoutée.`);
    }
    await reload();
    setFnOpen(false);
  };

  const handleSalarySave = async () => {
    if (!salaryEdit) return;
    await saveStaffSalary({
      salaryId: salaryEdit.id,
      personId: salaryEdit.personId,
      cycleId: salaryEdit.cycleId,
      amount: Number(salaryEdit.amount) || 0,
    });
    await reload();
    notify("Salaire mis à jour.");
    setSalaryEdit(null);
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Personnel non enseignant"
        subtitle={`${staffList.length} membres • ${functions.length} fonctions définies`}
        icon="ri-team-line"
        actions={
          tab === "liste" ? (
            <Button icon="ri-user-add-line" onClick={() => openForm(null)}>
              Ajouter un membre
            </Button>
          ) : (
            <Button
              icon="ri-add-line"
              onClick={() => {
                setFnEditing(null);
                setFnName("");
                setFnOpen(true);
              }}
            >
              Ajouter une fonction
            </Button>
          )
        }
      />

      <div className="mb-5">
        <Tabs
          tabs={[
            { id: "liste", label: "Liste du personnel", icon: "ri-list-check-2", count: staffList.length },
            { id: "fonctions", label: "Fonctions", icon: "ri-price-tag-3-line", count: functions.length },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === "liste" && (
        <>
          <div className="mb-6">
            <div className="relative sm:max-w-md">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher un membre (nom ou téléphone)..."
                className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <Card padded={false}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                    <th className="px-4 py-3 font-medium">Membre</th>
                    <th className="px-4 py-3 font-medium">Fonction</th>
                    <th className="px-4 py-3 font-medium">Embauche</th>
                    <th className="px-4 py-3 font-medium">Salaire</th>
                    <th className="px-4 py-3 font-medium">Statut</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-14 text-center">
                        <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                          <i className="ri-user-search-line text-2xl"></i>
                        </span>
                        <p className="text-sm text-foreground-600">Aucun membre ne correspond à votre recherche.</p>
                      </td>
                    </tr>
                  ) : (
                    page.items.map((m) => {
                      const sal = salaryOf(m.id);
                      return (
                        <tr key={m.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar lastName={m.lastName} firstName={m.firstName} src={m.photo} size="sm" />
                              <div>
                                <p className="font-medium text-foreground-900">
                                  {m.firstName} {m.lastName}
                                </p>
                                <p className="text-xs text-foreground-500">{m.phone}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge tone="secondary">{fnName_of(m.functionId)}</Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-foreground-600">{formatDate(m.hireDate)}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() =>
                                setSalaryEdit({
                                  id: sal?.id,
                                  personId: m.id,
                                  cycleId: m.cycleId,
                                  amount: String(sal?.amount ?? ""),
                                  name: `${m.firstName} ${m.lastName}`,
                                })
                              }
                              className="inline-flex items-center gap-1.5 rounded-md border border-background-200 bg-background-100/60 px-2.5 py-1 text-xs font-medium text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-2-line text-sm text-primary-600"></i>
                              {sal ? formatMoney(sal.amount) : "Définir"}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <Badge tone={EMPLOYMENT_STATUS_TONE[m.status]}>{EMPLOYMENT_STATUS_LABEL[m.status]}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => openForm(m)}
                                className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
                                aria-label="Modifier"
                              >
                                <i className="ri-edit-line"></i>
                              </button>
                              <button
                                onClick={() => setToDelete(m)}
                                className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                                aria-label="Supprimer"
                              >
                                <i className="ri-delete-bin-6-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Pagination pager={page} className="border-t border-background-200" />
          </Card>
        </>
      )}

      {tab === "fonctions" && (
        <Card>
          <SectionTitle
            icon="ri-price-tag-3-line"
            title="Fonctions du personnel"
            subtitle="Ajoutez, modifiez ou supprimez les fonctions disponibles"
            action={
              <Button
                size="sm"
                variant="outline"
                icon="ri-add-line"
                onClick={() => {
                  setFnEditing(null);
                  setFnName("");
                  setFnOpen(true);
                }}
              >
                Ajouter
              </Button>
            }
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {functions.map((f) => {
              const count = staffList.filter((s) => s.functionId === f.id).length;
              return (
                <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100/50 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-price-tag-3-line text-base"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground-900">{f.name}</p>
                      <p className="text-xs text-foreground-500">{plural(count, "membre")}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => {
                        setFnEditing(f.id);
                        setFnName(f.name);
                        setFnOpen(true);
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                      aria-label="Modifier la fonction"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setFnToDelete(f.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                      aria-label="Supprimer la fonction"
                    >
                      <i className="ri-delete-bin-6-line text-sm"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? "Modifier le membre" : "Ajouter un membre du personnel"}
        icon="ri-user-add-line"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={handleSave}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <PhotoUploader
              value={form.photo}
              onChange={(photo) => setForm({ ...form, photo })}
              lastName={form.lastName}
              firstName={form.firstName}
            />
          </div>
          <Input label="Nom" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          <Input label="Prénom" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          <Select label="Sexe" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value as Staff["gender"] })}>
            <option value="M">Masculin</option>
            <option value="F">Féminin</option>
          </Select>
          <Select label="Fonction" value={form.functionId} onChange={(e) => setForm({ ...form, functionId: e.target.value })}>
            {functions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
          <Select label="Cycle de rattachement" value={form.cycleId} onChange={(e) => setForm({ ...form, cycleId: e.target.value })}>
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          <Input label="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Date d'embauche" type="date" value={form.hireDate} onChange={(e) => setForm({ ...form, hireDate: e.target.value })} />
          <Select label="Statut" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Staff["status"] })}>
            {EMPLOYMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {EMPLOYMENT_STATUS_LABEL[status]}
              </option>
            ))}
          </Select>
        </div>
      </Modal>

      <Modal
        open={fnOpen}
        onClose={() => setFnOpen(false)}
        title={fnEditing ? "Modifier la fonction" : "Nouvelle fonction"}
        icon="ri-price-tag-3-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setFnOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={handleFnSave}>
              Enregistrer
            </Button>
          </>
        }
      >
        <Input label="Nom de la fonction" required value={fnName} onChange={(e) => setFnName(e.target.value)} />
      </Modal>

      <Modal
        open={salaryEdit !== null}
        onClose={() => setSalaryEdit(null)}
        title="Salaire individuel"
        subtitle={salaryEdit?.name}
        icon="ri-wallet-3-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setSalaryEdit(null)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={handleSalarySave}>
              Enregistrer
            </Button>
          </>
        }
      >
        <Input
          label={`Montant mensuel (${currencySymbol()})`}
          type="number"
          value={salaryEdit?.amount ?? ""}
          onChange={(e) => setSalaryEdit((p) => (p ? { ...p, amount: e.target.value } : p))}
          hint="Le salaire est propre à chaque personne."
        />
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer le membre"
        message={`Supprimer définitivement la fiche de ${toDelete?.firstName} ${toDelete?.lastName} ?`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteStaff(toDelete.id);
            await reload();
            notify("Membre supprimé.", "info");
          }
        }}
      />

      <ConfirmDialog
        open={fnToDelete !== null}
        title="Supprimer la fonction"
        message="Cette fonction sera retirée. Les membres concernés devront être réaffectés."
        confirmLabel="Supprimer"
        onClose={() => setFnToDelete(null)}
        onConfirm={async () => {
          if (fnToDelete) {
            await deleteStaffFunction(fnToDelete);
            await reload();
            notify("Fonction supprimée.", "info");
          }
        }}
      />
    </div>
  );
}